/**
 * @class History
 * Generic history object used for loading / saving states
 */
export default class History {
    states: any[];
    index: number;
    initialState: any;
    private updateFn: Function;

    /**
     * @constructor
     * 
     * @param initialState initial state object
     * @param updateFn update function for when history state changes
     */
    constructor(initialState: any, updateFn: Function) {
        this.states = [];
        this.states.push(initialState);
        this.initialState = initialState;
        this.index = 0;            
        this.updateFn = updateFn;
    }
    
    /**
     * update this history object after its settings have been changed.
     * 
     * @returns this 
     */
    update(): History {
        if (this.index >= this.states.length) {
            this.index = this.states.length-1;
        } else if(this.index < 0) {
            this.index = 0;
        }

        this.updateFn(this.states[this.index]);

        return this;
    }
    
    /**
     * add a new state at the current index, removing anything after it
     */
    add(newState: any) {
        this.states.splice(++this.index, this.states.length-this.index, newState);
        this.updateFn(this.states[this.index]);
    }
    
    /**
     * set the current state to the index given
     * 
     * @param index the new index
     * 
     * @returns this 
     */
    setIndex(index: number): History {
        if (index >= this.states.length) {
            index = this.states.length-1;
        } else if(index < 0) {
            index = 0;
        }

        if (this.index != index) {
            this.index = index;
            return this.update();
        } else {
            return this;
        }
    }
    
    /**
     * go back or forward the number of steps given
     * 
     * @param steps the number of steps to move
     * 
     * @returns this 
     */
    go(steps: number): History {
        return this.setIndex(steps + this.index);
    }
    
    /**
     * alias for go(1)
     * 
     * @returns this 
     */
    next(): History {
        return this.go(1);
    }
    
    /**
     * alias for go(-1)
     * 
     * @returns this 
     */
    prev(): History {
        return this.go(-1);
    }
    
    /**
     * go to first state
     * 
     * @returns this 
     */
    first(): History {
        return this.setIndex(0);
    }
    
    /**
     * go to last state
     * 
     * @returns this 
     */
    last(): History {
        return this.setIndex(this.states.length-1);
    }
    
    /**
     * determine if there are states after the current one
     * 
     * @returns true if there are, false otherwise  
     */
    hasNext(): boolean {
        return (this.index+1) < this.states.length;
    }
    
    /**
     * determine if there are states before the current one
     * 
     * @returns true if there are, false otherwise 
     */
    hasPrev(): boolean {
        return !!this.index;
    }

    /**
     * This will reset states to its initial state. We don't support multiple edit actions
     */
    clear() {
        this.states = [];
        this.states.push(this.initialState);
        this.index = 0;
    }
}
